import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
	ActivatedRoute,
	NavigationEnd,
	ParamMap,
	Params,
	Router,
} from '@angular/router';
import { EMPTY, Observable, Subscription, filter } from 'rxjs';

export interface UrlQueryActionConfig {
	queryParam: string;
	value?: string | readonly string[];
	onMatch: (
		value: string | null,
		queryParams: ParamMap,
	) => void | Promise<void>;
	once?: boolean;
}

export interface UrlQueryModalContext<TData = unknown> {
	modalKey: string;
	modalId: string | null;
	queryParams: ParamMap;
	data: TData;
}

export interface UrlQueryModalConfig<TData = unknown> {
	modalKey: string;
	// this might not be necessary, but it allows for more flexibility in case we want to use a different query param for the modal key
	modalQueryParam?: string;
	idQueryParam?: string;
	once?: boolean;
	resolveData?: (
		modalId: string | null,
		queryParams: ParamMap,
	) => TData | Promise<TData>;
	open: (context: UrlQueryModalContext<TData>) => void | Promise<void>;
}

export interface UrlQueryModalRegistrationService {
	watchModalRoute(urlQueryActionService: UrlQueryActionService): Subscription;
}

export interface UrlQueryOpenModalConfig {
	modalKey: string;
	queryParams?: Params;
	modalQueryParam?: string;
	currentParamsMatch?: (params: Params) => boolean;
	openModal$: () => Observable<boolean>;
}

@Injectable({
	providedIn: 'root',
})
export class UrlQueryActionService implements OnDestroy {
	private readonly subscriptions = new Subscription();
	private readonly completedActions = new Set<string>();

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		@Inject(DOCUMENT) private readonly document: Document,
	) {}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	init(): void {
		// Intentionally empty. Consumers register query-driven modal handlers as
		// their owning components or services initialize.
	}

	public getParams = () => {
		const queryParams = this.getCurrentQueryParams();
		return queryParams['params'];
	};

	public buildModalParams = (
		modalKey: string,
		queryParams: Params = {},
		modalQueryParam = 'modalKey',
	): Params => ({
		[modalQueryParam]: modalKey,
		...queryParams,
	});

	public hasMatchingParams = (expectedQueryParams: Params): boolean => {
		const currentParams = this.getParams();

		return Object.entries(expectedQueryParams).every(([key, value]) => {
			return (
				this.normalizeParamValue(currentParams[key]) ===
				this.normalizeParamValue(value)
			);
		});
	};

	public updateParams = (
		queryParams: Params,
		relativeRoute: ActivatedRoute = this.route,
		replaceUrl = true,
	): Promise<boolean> => {
		return this.router.navigate([], {
			relativeTo: relativeRoute,
			queryParams,
			queryParamsHandling: 'merge',
			replaceUrl,
		});
	};

	public openModalByQuery$ = ({
		modalKey,
		queryParams = {},
		modalQueryParam = 'modalKey',
		currentParamsMatch,
		openModal$,
	}: UrlQueryOpenModalConfig): Observable<boolean> => {
		const modalParams = this.buildModalParams(
			modalKey,
			queryParams,
			modalQueryParam,
		);
		const paramsMatch = currentParamsMatch
			? currentParamsMatch(this.getParams())
			: this.hasMatchingParams(modalParams);

		if (!paramsMatch) {
			this.updateParams(modalParams, undefined, false).catch((error: unknown) => {
				console.error(
					'UrlQueryActionService failed to update modal params',
					error,
				);
			});
			return EMPTY;
		}

		return openModal$();
	};

	public clickSelector = (selector: string): void => {
		(this.document.querySelector(selector) as HTMLElement | null)?.click();
	};

	watch = (actions: readonly UrlQueryActionConfig[]): Subscription => {
		const subscription = new Subscription();
		let runQueue = Promise.resolve();
		const runWatchedActions = () => {
			runQueue = runQueue
				.then(() => this.runActions(actions))
				.catch((error: unknown) => {
					console.error('UrlQueryActionService failed to run actions', error);
				});
		};

		runWatchedActions();
		subscription.add(
			this.router.events
				.pipe(filter((event) => event instanceof NavigationEnd))
				.subscribe(() => {
					runWatchedActions();
				}),
		);

		this.subscriptions.add(subscription);

		return subscription;
	};

	watchModals = <TData>(
		actions: readonly UrlQueryModalConfig<TData>[],
	): Subscription => {
		return this.watch(
			actions.map((action) => ({
				queryParam: action.modalQueryParam ?? 'modal',
				value: action.modalKey,
				once: action.once,
				onMatch: async (_value, queryParams) => {
					const modalId = queryParams.get(action.idQueryParam ?? 'id');
					const data = action.resolveData
						? await action.resolveData(modalId, queryParams)
						: (undefined as TData);

					await action.open({
						modalKey: action.modalKey,
						modalId,
						queryParams,
						data,
					});
				},
			})),
		);
	};

	registerModals = (
		modalServices: readonly UrlQueryModalRegistrationService[],
	): Subscription => {
		const subscription = new Subscription();

		modalServices.forEach((modalService) => {
			subscription.add(modalService.watchModalRoute(this));
		});

		this.subscriptions.add(subscription);

		return subscription;
	};

	async runActions(actions: readonly UrlQueryActionConfig[]): Promise<void> {
		const queryParams = this.getCurrentQueryParams();

		await actions.reduce<Promise<void>>(async (previousAction, action) => {
			await previousAction;

			if (!this.matches(action, queryParams)) {
				return;
			}

			const actionKey = this.getActionKey(action);
			if (action.once !== false && this.completedActions.has(actionKey)) {
				return;
			}

			await action.onMatch(queryParams.get(action.queryParam), queryParams);

			if (action.once !== false) {
				this.completedActions.add(actionKey);
			}
		}, Promise.resolve());
	}

	private getCurrentQueryParams(): ParamMap {
		let route = this.route.snapshot;

		while (route.firstChild != null) {
			route = route.firstChild;
		}

		return route.queryParamMap;
	}

	private matches(
		action: UrlQueryActionConfig,
		queryParams: ParamMap,
	): boolean {
		if (!queryParams.has(action.queryParam)) {
			return false;
		}

		if (action.value == null) {
			return true;
		}

		const currentValue = queryParams.get(action.queryParam);
		const validValues = Array.isArray(action.value)
			? action.value
			: [action.value];

		return currentValue != null && validValues.includes(currentValue);
	}

	private getActionKey(action: UrlQueryActionConfig): string {
		const value = Array.isArray(action.value)
			? action.value.join(',')
			: (action.value ?? '*');

		return `${action.queryParam}:${value}`;
	}

	private normalizeParamValue(value: unknown): string | null {
		if (value == null || value === '') {
			return null;
		}

		if (Array.isArray(value)) {
			return value.map((item) => this.normalizeParamValue(item)).join(',');
		}

		return String(value);
	}
}
